/**
 * CAP detail — the "one screen" page.
 *
 * Sections live in a single vertical stream. A user scrolls top-to-bottom and
 * can read everything; only the sign-off actions are role-gated. The sections
 * are deliberately labeled in plain English:
 *   Source document — the audit report or document that started the CAP
 *   What happened   — the deficiency
 *   Why it happened — the iterative why-chain and root cause(s)
 *   Remediation     — corrective + preventive steps
 *   Evidence        — files, notes, and links proving the remediation worked
 *   Discussion      — threaded comments
 *   Team / FDR access / Done
 *
 * Each interactive widget receives `data`, `onRefresh`, and (where relevant)
 * `user`. After every successful mutation a widget calls `onRefresh()` so the
 * page reflects the change without a manual reload.
 */

import React, { useCallback, useEffect, useState } from 'react';
import useAgentProgress from '../../lib/useAgentProgress';
import AgentThinkingPanel from '../../components/AgentThinkingPanel';
import WhatHappenedSection from './components/WhatHappenedSection';
import WhysSection from './components/WhysSection';
import MilestonesSection from './components/MilestonesSection';
import EvidenceSection from './components/EvidenceSection';
import CommentsSection from './components/CommentsSection';
import SignOffSection from './components/SignOffSection';
import FdrInviteSection from './components/FdrInviteSection';
import TeamSection from './components/TeamSection';
import SourceDocumentSection from './components/SourceDocumentSection';
import HealthSection from './components/HealthSection';
import Pill from './components/Pill';

const API = process.env.REACT_APP_API_URL;

function StatusPill({ status }) {
  const tones = {
    open:        'bg-amber-50 text-amber-800 ring-amber-200',
    in_progress: 'bg-blue-50 text-blue-800 ring-blue-200',
    validating:  'bg-violet-50 text-violet-800 ring-violet-200',
    approved:    'bg-emerald-50 text-emerald-800 ring-emerald-200',
    closed:      'bg-slate-100 text-slate-700 ring-slate-200',
    cancelled:   'bg-slate-100 text-slate-500 ring-slate-200',
  };
  const labels = {
    open: 'Open', in_progress: 'In progress', validating: 'Validating',
    approved: 'Approved', closed: 'Closed', cancelled: 'Cancelled',
  };
  return (
    <Pill tone={tones[status] || tones.open}>{labels[status] || status}</Pill>
  );
}

export default function CapDetail({ user, capId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/pcap/caps/${capId}`, { credentials: 'include' });
      const d = await res.json();
      if (!res.ok || !d.success) throw new Error(d.message || 'Failed to load CAP');
      setData(d);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [capId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [load]);

  // Section components call this after every successful mutation.
  const onRefresh = useCallback(() => { load(); }, [load]);

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 pt-8 text-sm text-slate-500">Loading…</div>;
  }
  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-6 pt-8">
        <button onClick={onBack} className="text-sm text-slate-500 hover:text-slate-800 mb-3">← Back</button>
        <div className="rounded-lg bg-red-50 ring-1 ring-red-200 p-3 text-sm text-red-800">{error}</div>
      </div>
    );
  }
  if (!data?.cap) return null;

  const { cap } = data;

  return (
    <div className="max-w-6xl mx-auto px-6 pt-10 pb-20">
      <button
        onClick={onBack}
        className="text-sm text-slate-500 hover:text-slate-900 mb-6 inline-flex items-center gap-1.5 transition"
      >
        ← Back
      </button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <StatusPill status={cap.status} />
          {cap.is_fdr_scope && (
            <Pill tone="bg-orange-50 text-orange-800 ring-orange-200">FDR scope</Pill>
          )}
        </div>
        <h1 className="text-[28px] font-semibold text-slate-900 leading-[1.15] tracking-tight">{cap.title}</h1>
        {cap.due_date && (
          <p className="text-[13px] text-slate-500 mt-2">
            Due {new Date(cap.due_date).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
          </p>
        )}
      </div>

      {/* Agent activity for the in-flight CAP agent (root-cause inference,
          remediation drafting). Renders nothing until the CAP agent emits
          events. */}
      <CapAgentActivity capId={capId} />

      <div className="space-y-5">
        <HealthSection data={data} onRefresh={onRefresh} />
        <SourceDocumentSection data={data} onRefresh={onRefresh} />
        <WhatHappenedSection data={data} onRefresh={onRefresh} />
        <WhysSection data={data} onRefresh={onRefresh} />
        <MilestonesSection data={data} onRefresh={onRefresh} />
        <EvidenceSection data={data} onRefresh={onRefresh} />
        <CommentsSection data={data} onRefresh={onRefresh} />
        <TeamSection data={data} onRefresh={onRefresh} />
        <FdrInviteSection data={data} onRefresh={onRefresh} />
        <SignOffSection data={data} onRefresh={onRefresh} user={user} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CapAgentActivity — polls /api/agent-progress for the CAP agent's events
// on this CAP. Mirrors the OracleHome DocumentAgentActivity pattern.
// ---------------------------------------------------------------------------
function CapAgentActivity({ capId }) {
  const { events, status } = useAgentProgress('cap', capId);
  if (!events || events.length === 0) return null;
  return <AgentThinkingPanel events={events} status={status} title="Activity" />;
}
